import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  FileSpreadsheet, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  RefreshCw, 
  ExternalLink,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Import = () => {
  const [sheetId, setSheetId] = useState("");
  const [tabName, setTabName] = useState("Sheet1");
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [importsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/baseline/imports`),
        axios.get(`${API}/settings`)
      ]);
      setImports(importsRes.data);
      setSettings(settingsRes.data);
      // Pre-fill from settings if available
      if (settingsRes.data?.baseline_sheet_id) {
        setSheetId(settingsRes.data.baseline_sheet_id);
      }
      if (settingsRes.data?.baseline_sheet_tab) {
        setTabName(settingsRes.data.baseline_sheet_tab);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    if (!sheetId.trim()) {
      toast.error("Please enter a Spreadsheet ID");
      return;
    }
    
    setSyncing(true);
    setResult(null);
    
    try {
      // Save the baseline sheet config to settings
      await axios.put(`${API}/settings`, {
        baseline_sheet_id: sheetId,
        baseline_sheet_tab: tabName
      });

      const response = await axios.post(`${API}/baseline/sync-from-sheets`, {
        sheet_id: sheetId,
        tab_name: tabName
      });
      
      setResult(response.data);
      
      if (response.data.valid_rows > 0) {
        toast.success(`Imported ${response.data.valid_rows} campers from Google Sheets`);
      } else if (response.data.total_rows === 0) {
        toast.warning("No data found in sheet");
      } else {
        toast.warning("No new campers imported (all duplicates or invalid)");
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
      setResult({ error: true, message: error.response?.data?.detail || "Import failed" });
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    try {
      return new Date(isoString).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
      });
    } catch {
      return isoString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-forest animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 fade-in max-w-4xl" data-testid="import-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-stone-900 tracking-tight">Import Baseline</h1>
        <p className="text-stone-500 mt-1">Sync your existing camper list from Google Sheets for duplicate protection</p>
      </div>

      {/* Sync from Sheets Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-forest" />
            Sync from Google Sheets
          </CardTitle>
          <CardDescription>
            Expected columns: Last Name, First Name, Person ID (column names are flexible)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4">
            <div>
              <Label htmlFor="baseline-sheet-id" className="text-sm font-medium">
                Baseline Spreadsheet ID
              </Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  id="baseline-sheet-id"
                  value={sheetId}
                  onChange={(e) => setSheetId(e.target.value)}
                  placeholder="1g_pTpFrtyJruapEBBdm3Fp73z_-Xg19n3fMPcIYvJWs"
                  className="font-mono text-sm"
                  data-testid="baseline-sheet-id-input"
                />
                {sheetId && (
                  <Button variant="outline" size="icon" asChild>
                    <a 
                      href={`https://docs.google.com/spreadsheets/d/${sheetId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>
              <p className="text-xs text-stone-500 mt-1">
                Found in the URL: /spreadsheets/d/<strong>SPREADSHEET_ID</strong>/edit
              </p>
            </div>

            <div>
              <Label htmlFor="baseline-tab-name" className="text-sm font-medium">
                Sheet Tab Name
              </Label>
              <Input
                id="baseline-tab-name"
                value={tabName}
                onChange={(e) => setTabName(e.target.value)}
                placeholder="Sheet1"
                className="mt-1.5"
                data-testid="baseline-tab-input"
              />
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-earth mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-stone-800">Before syncing:</p>
                <ul className="mt-1 text-stone-600 space-y-1">
                  <li>Ensure your Google Apps Script web app is deployed and URL is set in Settings</li>
                  <li>The sheet must have headers in the first row</li>
                  <li>Supported column names: Last Name, First Name, Person ID (case insensitive)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sync Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSync}
              disabled={!sheetId.trim() || syncing}
              className="bg-forest hover:bg-forest-dark text-white font-bold py-2 px-8 rounded-md shadow-lg transition-all flex items-center gap-2"
              data-testid="sync-baseline-btn"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Sync Baseline from Sheets
                </>
              )}
            </Button>
          </div>

          {/* Result Summary */}
          {result && !result.error && (
            <Card className="bg-stone-50 border-stone-200" data-testid="import-result">
              <CardContent className="pt-6">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-stone-800">{result.total_rows}</p>
                    <p className="text-xs uppercase tracking-wide text-stone-500">Total Rows</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-signal-success">{result.valid_rows}</p>
                    <p className="text-xs uppercase tracking-wide text-stone-500">Imported</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-signal-warning">{result.duplicates_ignored}</p>
                    <p className="text-xs uppercase tracking-wide text-stone-500">Duplicates</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-signal-error">{result.invalid_rows}</p>
                    <p className="text-xs uppercase tracking-wide text-stone-500">Invalid</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {result && result.error && (
            <Card className="bg-signal-error/5 border-signal-error">
              <CardContent className="pt-6 flex items-center gap-3">
                <XCircle className="w-5 h-5 text-signal-error" />
                <p className="text-stone-800">{result.message}</p>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Import History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading">Import History</CardTitle>
          <CardDescription>Previous baseline imports</CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          {imports.length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead className="text-center">Imported</TableHead>
                  <TableHead className="text-center">Duplicates</TableHead>
                  <TableHead className="text-center">Invalid</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {imports.map((imp) => (
                  <TableRow key={imp.id}>
                    <TableCell className="font-mono text-sm max-w-[200px] truncate" title={imp.filename}>
                      {imp.filename.startsWith('sheets:') ? (
                        <span className="flex items-center gap-1">
                          <FileSpreadsheet className="w-4 h-4 text-signal-info" />
                          {imp.filename.replace('sheets:', '').split('/')[1] || 'Sheet1'}
                        </span>
                      ) : (
                        imp.filename
                      )}
                    </TableCell>
                    <TableCell className="text-center">{imp.total_rows}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-signal-success/10 text-signal-success border-signal-success/30">
                        {imp.valid_rows}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-signal-warning/10 text-signal-warning border-signal-warning/30">
                        {imp.duplicates_ignored}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-signal-error/10 text-signal-error border-signal-error/30">
                        {imp.invalid_rows}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-stone-500">{formatDate(imp.imported_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-stone-500">
              <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No imports yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
